import {FC, ReactNode, useMemo, useCallback} from "react";
import {useTranslation} from "react-i18next";
import {Badge, BadgeVariant} from "@interstate/components/Badge";
import {
  InformationCircleIcon,
  ArrowTopRightOnSquareIcon,
  MinusCircleIcon,
  PlusCircleIcon
} from "@interstate/components/Icons";
import {useInterstateToken} from "@interstate/components/InterstateThemeProvider";
import {List, ListItem} from "@interstate/components/List";
import {Tooltip} from "@interstate/components/Tooltip";
import {BoidAssignmentState, BoidAssignmentStatus, statusFor} from "../model";
import {groupBoidAssignmentStatesByType} from "../transforms";
import {useOperationalEntity} from "../../context";
import {
  ASSIGN_BOID_ENTITY,
  UNASSIGN_BOID_ENTITY,
  useAuthorization
} from "../../../access";
import {useNavigation} from "../../../navigation";
import {EntitySourceType} from "../../EntitySourceType";

type BoidAssignmentStateCallback<E = any> = (
  state: BoidAssignmentState,
  event: E
) => void;

export interface BoidEditListProps {
  boidAssignmentStates: BoidAssignmentState[];
  onAssignBoid: BoidAssignmentStateCallback;
  onUnassignBoid: BoidAssignmentStateCallback;
  working?: boolean;
}

export const BoidEditList: FC<BoidEditListProps> = ({
  boidAssignmentStates,
  onAssignBoid,
  onUnassignBoid,
  working
}) => {
  const {t} = useTranslation();
  const token = useInterstateToken();
  const {entity} = useOperationalEntity();

  /**
   * Helper function to convert a Boid's type (e.g. DDC or FUS) into its translation (e.g. Dealer.com or Fusion)
   */
  const translateBoidType = useCallback(
    (type: string): string => {
      return t(`entity-detail.business-operation-types.${type}`);
    },
    [t]
  );

  const granted = useAuthorization();
  const canAssignBoid = useMemo<boolean>(
    () => granted([ASSIGN_BOID_ENTITY]),
    [granted]
  );
  const canUnassignBoid = useMemo<boolean>(
    () => granted([UNASSIGN_BOID_ENTITY]),
    [granted]
  );
  const {navigateToEntity} = useNavigation();

  const allBoidTypeGroups = useMemo(
    () =>
      groupBoidAssignmentStatesByType(boidAssignmentStates)
        .sort((a, b) =>
          translateBoidType(a.type).localeCompare(translateBoidType(b.type))
        )
        .map(group => ({
          ...group,
          states: group.states.sort((a, b) => a.id.localeCompare(b.id))
        })),
    [boidAssignmentStates, translateBoidType]
  );

  const navigateToAssignedEntity: BoidAssignmentStateCallback = async (
    state
  ): Promise<void> => {
    navigateToEntity(state.assignedTo!, EntitySourceType.OPERATIONAL);
  };

  const createBoidBadgeListItem = (
    state: BoidAssignmentState,
    permitted: boolean,
    onClickCallback: BoidAssignmentStateCallback,
    status: BoidAssignmentStatus,
    variant: BadgeVariant,
    startIcon: ReactNode,
    endIcon: ReactNode
  ) => {
    const {type, id} = state;
    return (
      <li
        key={`${type}-${id}`}
        id={`${type}-${id}-boid-badge-item`}
        data-testid={`${type}-${id}-boid-badge-item`}
        className={`boid-badge-item boid-badge-item--${status}`}
        onClick={event => {
          if (permitted) {
            onClickCallback(state, event);
          }
        }}>
        <Badge
          id={`${type}-${id}-boid-badge`}
          data-testid={`${type}-${id}-boid-badge`}
          className={`boid-badge boid-badge--${status}${permitted ? " clickable" : ""}`}
          variant={variant}
          startIcon={startIcon}
          endIcon={permitted ? endIcon : undefined}>
          {id}
        </Badge>
      </li>
    );
  };

  const createAssignedBoidListItem = (state: BoidAssignmentState) => {
    const {type, id} = state;
    return createBoidBadgeListItem(
      state,
      canUnassignBoid,
      onUnassignBoid,
      "assigned",
      "success",
      <Tooltip
        id={`${type}-${id}-info-tooltip`}
        data-testid={`${type}-${id}-info-tooltip`}
        toolTipContent={t("entity-detail.boid-assignment-status.assigned")}
        position={"top"}
        size={"small"}>
        <InformationCircleIcon
          data-testid={`${type}-${id}-info-icon`}
          color={token("sem.color.border.success")}
        />
      </Tooltip>,
      <MinusCircleIcon
        data-testid={`${type}-${id}-minus-icon`}
        color={token("sem.color.border.success")}
      />
    );
  };

  const createOrphanedBoidListItem = (state: BoidAssignmentState) => {
    const {type, id} = state;
    return createBoidBadgeListItem(
      state,
      canUnassignBoid,
      onUnassignBoid,
      "orphaned",
      "info",
      <Tooltip
        id={`${type}-${id}-info-tooltip`}
        data-testid={`${type}-${id}-info-tooltip`}
        toolTipContent={t("entity-detail.boid-assignment-status.orphaned")}
        position={"top"}
        size={"small"}>
        <InformationCircleIcon
          data-testid={`${type}-${id}-info-icon`}
          color={token("sem.color.border.info")}
        />
      </Tooltip>,
      <MinusCircleIcon
        data-testid={`${type}-${id}-minus-icon`}
        color={token("sem.color.border.info")}
      />
    );
  };

  const createAvailableBoidListItem = (state: BoidAssignmentState) => {
    const {type, id} = state;
    return createBoidBadgeListItem(
      state,
      canAssignBoid,
      onAssignBoid,
      "available",
      "light",
      <Tooltip
        id={`${type}-${id}-info-tooltip`}
        data-testid={`${type}-${id}-info-tooltip`}
        toolTipContent={t("entity-detail.boid-assignment-status.available")}
        position={"top"}
        size={"small"}>
        <InformationCircleIcon
          data-testid={`${type}-${id}-info-icon`}
          color={token("sem.color.border.success")}
        />
      </Tooltip>,
      <PlusCircleIcon
        data-testid={`${type}-${id}-plus-icon`}
        color={token("sem.color.border.success")}
      />
    );
  };

  const createUnavailableBoidListItem = (state: BoidAssignmentState) => {
    const {type, id} = state;
    return createBoidBadgeListItem(
      state,
      true,
      navigateToAssignedEntity,
      "unavailable",
      "error",
      <Tooltip
        id={`${type}-${id}-info-tooltip`}
        data-testid={`${type}-${id}-info-tooltip`}
        toolTipContent={t("entity-detail.boid-assignment-status.unavailable")}
        position={"top"}
        size={"small"}>
        <InformationCircleIcon
          data-testid={`${type}-${id}-info-icon`}
          color={token("sem.color.border.error")}
        />
      </Tooltip>,
      <ArrowTopRightOnSquareIcon
        data-testid={`${type}-${id}-link-icon`}
        color={token("sem.color.border.error")}
      />
    );
  };

  const toAssignmentListItem = (state: BoidAssignmentState) => {
    switch (statusFor(state, entity.id)) {
      case "assigned":
        return createAssignedBoidListItem(state);
      case "orphaned":
        return createOrphanedBoidListItem(state);
      case "available":
        return createAvailableBoidListItem(state);
      case "unavailable":
        return createUnavailableBoidListItem(state);
    }
  };

  return (
    <List
      id={"boid-allocations-list"}
      data-testid={"boid-allocations-list"}
      className={"boid-allocations-list"}
      sx={working ? {pointerEvents: "none"} : undefined}>
      {allBoidTypeGroups.map(({type, states}) => (
        <ListItem
          key={type}
          id={`${type}-boid-allocations-list-item`}
          data-testid={`${type}-boid-allocations-list-item`}
          className={"boid-allocations-list-item"}>
          <h4 className={"business-operation-type-name"}>
            {translateBoidType(type)}
          </h4>
          {states.length > 0 ? (
            <ul
              id={`${type}-boid-badge-list`}
              data-testid={`${type}-boid-badge-list`}
              className={"boid-badge-list"}>
              {states.map(toAssignmentListItem)}
            </ul>
          ) : (
            <span
              className={"no-assignments"}
              data-testid={`no-${type}-assignments`}>
              {t("entity-detail.business-operation-types.no-assignments")}
            </span>
          )}
        </ListItem>
      ))}
    </List>
  );
};
